import React, { useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { UserForm, UserFormRef } from 'src/pages/Settings/user/UserForm';
import { trpc } from 'src/trpc';
import { UserZ } from '@libs/schemas';
import { useToast } from 'src/components/useToast';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { User } from 'src/trpc';

interface UserSheetProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
}

export const UserSheet: React.FC<UserSheetProps> = ({ isOpen, onClose, user }) => {
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();
  const formRef = useRef<UserFormRef>(null);
  const isEditing = !!user;

  const createUserMutation = trpc.admin.user.adminCreateUser.useMutation({
    onSuccess: () => {
      successToast({
        title: 'User created',
        description: 'New user has been successfully created',
      });
      utils.admin.user.adminGetAllUsers.invalidate();
      onClose();
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to create user',
        description: error.message || 'An error occurred while creating the user',
      });
    },
  });

  const updateUserMutation = trpc.admin.user.adminUpdateUser.useMutation({
    onSuccess: () => {
      successToast({
        title: 'User updated',
        description: 'User has been successfully updated',
      });
      utils.admin.user.adminGetAllUsers.invalidate();
      onClose();
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to update user',
        description: error.message || 'An error occurred while updating the user',
      });
    },
  });

  const handleCreateUser = async (userData: UserZ) => {
    // If the user is not a customer, they must have at least one organization
    if (
      (!userData.organizations || userData.organizations.length === 0) &&
      !userData.roles.includes('customer')
    ) {
      errorToast({
        title: 'Organization Required',
        description: 'Please select at least one organization before submitting.',
      });
      return;
    }

    createUserMutation.mutate(userData);
  };

  const handleUpdateUser = async (userData: UserZ) => {
    if (!user) return;

    // If the user is not a customer, they must have at least one organization
    if (
      (!userData.organizations || userData.organizations.length === 0) &&
      !userData.roles.includes('customer')
    ) {
      errorToast({
        title: 'Organization Required',
        description: 'Please select at least one organization before submitting.',
      });
      return;
    }

    updateUserMutation.mutate({ ...userData, _id: user._id });
  };

  const handleSubmitClick = async () => {
    if (formRef.current) {
      await formRef.current.submit();
    }
  };

  const handleClose = () => {
    const isPending = isEditing ? updateUserMutation.isPending : createUserMutation.isPending;
    if (!isPending) {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
      >
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'Edit User' : 'Create New User'}
                </SheetTitle>
              </div>
            </div>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className="overflow-y-auto px-6 py-6">
            <UserForm
              ref={formRef}
              onSubmit={isEditing ? handleUpdateUser : handleCreateUser}
              onCancel={handleClose}
              initialData={user || undefined}
              isEditing={isEditing}
            />
          </div>

          {/* Footer */}
          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isEditing ? updateUserMutation.isPending : createUserMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitClick}
                disabled={isEditing ? updateUserMutation.isPending : createUserMutation.isPending}
                className={cn(
                  'rounded-lg transition-all shadow-sm hover:shadow',
                  isEditing
                    ? updateUserMutation.isPending
                      ? 'bg-primary/50 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary/90'
                    : createUserMutation.isPending
                      ? 'bg-primary/50 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary/90',
                )}
              >
                {isEditing
                  ? updateUserMutation.isPending
                    ? 'Updating...'
                    : 'Update User'
                  : createUserMutation.isPending
                    ? 'Creating...'
                    : 'Create User'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
